import { useState, type FormEvent } from 'react';
import { Award, BadgePercent, Handshake, TrendingUp, Users, GraduationCap, ArrowUpRight, Wrench, Store, CheckCircle2 } from 'lucide-react';
import type { FormReceipt, PartnerApplicationRequest } from '@scada-ink/shared/cloud';
import { Button, Container, IconTile, Reveal, SectionHeading, CheckList } from '@/components/ui';
import { PageHero } from '@/components/sections';
import { Alert, Honeypot, SelectField, TextArea, TextField } from '@/components/form';
import { usePageTitle } from '@/components/layout/SiteLayout';
import { CERTIFICATIONS, MAX_PARTNER_DISCOUNT, PARTNER_TIERS } from '@/content/training';
import { cloudApi, errorMessage } from '@/lib/cloudApi';
import { INK_U_URL } from '@/lib/site';

function DiscountLadder() {
  return (
    <div className="glass-panel p-6 shadow-xl shadow-navy-900/5">
      <div className="flex items-center justify-between mb-5">
        <span className="text-sm font-semibold">Software discount by annual volume</span>
        <span className="text-xs text-text-muted">units / 12 months</span>
      </div>
      <div className="flex items-end gap-3 h-52">
        {PARTNER_TIERS.map((t, i) => (
          <div key={t.name} className="flex-1 flex flex-col items-center justify-end h-full">
            <div className="text-lg font-extrabold text-brand-primary">{t.discountPct}%</div>
            <div
              className="w-full rounded-t-lg bg-gradient-to-t from-brand-primary to-brand-secondary mt-1.5"
              style={{ height: `${(t.discountPct / MAX_PARTNER_DISCOUNT) * 70}%`, opacity: 0.55 + i * 0.15 }}
            />
            <div className="mt-2 text-xs font-semibold">{t.name}</div>
            <div className="text-[10px] text-text-muted">{t.maxUnits === null ? `${t.minUnits}+` : `${t.minUnits}–${t.maxUnits}`}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const EMPTY: PartnerApplicationRequest = {
  name: '',
  email: '',
  company: '',
  website: '',
  country: '',
  partnerType: 'integrator',
  expectedAnnualUnits: 5,
  certifiedStaff: 0,
  inkUEmails: '',
  notes: '',
};

function ApplicationForm() {
  const [form, setForm] = useState(EMPTY);
  const [trap, setTrap] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<FormReceipt | null>(null);

  const set = <K extends keyof PartnerApplicationRequest>(key: K, value: PartnerApplicationRequest[K]) => setForm((f) => ({ ...f, [key]: value }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSending(true);
    setError(null);
    try {
      setReceipt(await cloudApi.post<FormReceipt>('/api/public/partner-applications', { ...form, website_url: trap }));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSending(false);
    }
  }

  if (receipt) {
    return (
      <div className="glass-panel p-8 text-center">
        <CheckCircle2 size={40} className="mx-auto text-status-running" />
        <h3 className="mt-4 text-xl font-bold">Application received</h3>
        <p className="mt-2 text-text-secondary">
          Reference <span className="font-mono font-semibold text-text-primary">{receipt.reference}</span>. Our partner team will verify your certifications and be in touch within
          two business days.
        </p>
        <div className="mt-6">
          <Button href={INK_U_URL} variant="secondary">
            Keep working on certification <ArrowUpRight size={14} />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="relative glass-panel p-6 lg:p-8 space-y-5">
      <Honeypot value={trap} onChange={setTrap} />
      <div className="grid sm:grid-cols-2 gap-4">
        <TextField label="Your name" required value={form.name} onChange={(e) => set('name', e.target.value)} autoComplete="name" />
        <TextField label="Work email" type="email" required value={form.email} onChange={(e) => set('email', e.target.value)} autoComplete="email" />
        <TextField label="Company" required value={form.company} onChange={(e) => set('company', e.target.value)} autoComplete="organization" />
        <TextField label="Website" type="url" placeholder="https://" value={form.website} onChange={(e) => set('website', e.target.value)} />
        <TextField label="Country / region" required value={form.country} onChange={(e) => set('country', e.target.value)} autoComplete="country-name" />
        <SelectField
          label="Partner type"
          value={form.partnerType}
          onChange={(e) => set('partnerType', e.target.value as PartnerApplicationRequest['partnerType'])}
          options={[
            { value: 'integrator', label: 'System integrator' },
            { value: 'reseller', label: 'Reseller / distributor' },
            { value: 'both', label: 'Both' },
          ]}
        />
        <TextField
          label="Expected appliances sold per year"
          type="number"
          min={0}
          max={10000}
          required
          value={form.expectedAnnualUnits}
          onChange={(e) => set('expectedAnnualUnits', Number(e.target.value))}
        />
        <TextField
          label="Staff with both certifications"
          type="number"
          min={0}
          max={1000}
          required
          value={form.certifiedStaff}
          onChange={(e) => set('certifiedStaff', Number(e.target.value))}
          hint="Include people who are working toward them."
        />
      </div>
      <TextArea
        label="Ink U account emails"
        rows={2}
        value={form.inkUEmails}
        onChange={(e) => set('inkUEmails', e.target.value)}
        hint="So we can verify certification progress. One per line."
      />
      <TextArea label="Tell us about your business" rows={3} value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Industries, regions, typical project size…" />
      {error && <Alert kind="error">{error}</Alert>}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
        <p className="text-xs text-text-muted">We only use this to evaluate your application.</p>
        <Button type="submit" size="lg" disabled={sending} arrow>
          {sending ? 'Submitting…' : 'Submit application'}
        </Button>
      </div>
    </form>
  );
}

export function PartnersPage() {
  usePageTitle('Partner Program');
  return (
    <>
      <PageHero
        crumbs={[{ label: 'Partners' }]}
        eyebrow="Reseller & integrator program"
        title={`Get certified. Save up to ${MAX_PARTNER_DISCOUNT}% on Scada.Ink software.`}
        intro={`Resellers and system integrators who complete both Scada.Ink certifications — ${CERTIFICATIONS.map((c) => c.name.replace('Scada.Ink ', '')).join(' and ')} — buy software licenses at a discount that grows with sales volume, up to ${MAX_PARTNER_DISCOUNT}%.`}
        actions={
          <>
            <Button to="/partners#apply" size="lg" arrow>
              Apply to partner
            </Button>
            <Button href={INK_U_URL} size="lg" variant="secondary">
              <GraduationCap size={16} /> Start certification
            </Button>
          </>
        }
        visual={<DiscountLadder />}
      />

      <section className="py-20 lg:py-24">
        <Container>
          <SectionHeading eyebrow="How to qualify" title="Two certifications, then volume does the rest" align="center" />
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { icon: GraduationCap, color: '#ca8a04', title: '1. Earn the Associate credential', body: 'Complete Courses 1–12 on Ink U, pass the quizzes and the proctored Associate exam.' },
              { icon: Award, color: '#7c3aed', title: '2. Earn the Professional credential', body: 'Complete Courses 13–25, pass the quizzes and the Professional exam with its hands-on scenario.' },
              { icon: BadgePercent, color: '#16a34a', title: '3. Apply and start saving', body: 'With both credentials on staff you start at Certified pricing; your tier rises with units sold.' },
            ].map((s, i) => (
              <Reveal key={s.title} delay={i * 80}>
                <div className="glass-card h-full p-6">
                  <IconTile icon={s.icon} color={s.color} />
                  <h3 className="mt-4 font-semibold">{s.title}</h3>
                  <p className="mt-2 text-sm text-text-secondary leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <section id="tiers" className="scroll-mt-32 py-20 bg-white border-y border-glass-border">
        <Container>
          <SectionHeading
            eyebrow="Partner tiers"
            title="Your discount grows with your sales"
            intro="Tiers are based on appliance units sold in the trailing 12 months and reviewed each quarter. Discounts apply to Scada.Ink software licenses."
          />
          <div className="overflow-x-auto">
            <div className="grid grid-cols-4 gap-4 min-w-[860px]">
              {PARTNER_TIERS.map((t, i) => (
                <div
                  key={t.name}
                  className={`rounded-[var(--radius-glass)] p-6 border flex flex-col ${i === PARTNER_TIERS.length - 1 ? 'bg-navy-900 text-white border-navy-900' : 'bg-white border-glass-border'}`}
                >
                  <div className={`text-xs font-semibold uppercase tracking-[0.16em] ${i === PARTNER_TIERS.length - 1 ? 'text-brand-accent' : 'text-brand-primary'}`}>{t.name}</div>
                  <div className="mt-3 text-5xl font-extrabold tracking-tight">{t.discountPct}%</div>
                  <div className={`text-xs mt-1 ${i === PARTNER_TIERS.length - 1 ? 'text-slate-400' : 'text-text-muted'}`}>off software licenses</div>
                  <div className={`mt-4 text-sm font-semibold ${i === PARTNER_TIERS.length - 1 ? 'text-white' : ''}`}>
                    {t.maxUnits === null ? `${t.minUnits}+ units / year` : `${t.minUnits}–${t.maxUnits} units / year`}
                  </div>
                  <div className={`mt-4 pt-4 border-t ${i === PARTNER_TIERS.length - 1 ? 'border-white/10' : 'border-glass-border'}`}>
                    <CheckList items={t.benefits} dark={i === PARTNER_TIERS.length - 1} color={i === PARTNER_TIERS.length - 1 ? '#06b6d4' : '#16a34a'} />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <p className="mt-5 text-xs text-text-muted">
            All tiers require at least one person on staff holding both the Associate and Professional credentials, kept current.
          </p>
        </Container>
      </section>

      <section className="py-20">
        <Container>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Store, title: 'Resellers', body: 'Add an edge SCADA/MES appliance to your line card with margin that grows as you sell.' },
              { icon: Wrench, title: 'System integrators', body: 'Deliver projects faster on a platform your engineers are certified on — and keep the license margin.' },
              { icon: TrendingUp, title: 'Recurring revenue', body: 'Cloud services renew monthly on the appliances you deploy for your customers.' },
              { icon: Users, title: 'Shared leads', body: 'Gold and Platinum partners receive inquiries from their territory.' },
            ].map((b) => (
              <div key={b.title} className="glass-card p-6">
                <b.icon size={22} className="text-brand-primary" />
                <h3 className="mt-4 font-semibold">{b.title}</h3>
                <p className="mt-2 text-sm text-text-secondary leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section id="apply" className="scroll-mt-32 py-20 bg-white border-t border-glass-border">
        <Container>
          <div className="grid lg:grid-cols-[1fr_1.4fr] gap-12">
            <div>
              <Handshake size={30} className="text-brand-primary" />
              <h2 className="mt-5 text-3xl font-bold tracking-tight">Apply to the partner program</h2>
              <p className="mt-4 text-text-secondary leading-relaxed">
                You can apply before your team finishes certification — we will set you up with deal registration and a demo appliance, and your
                discount activates when both credentials are complete.
              </p>
              <div className="mt-8 space-y-3">
                {CERTIFICATIONS.map((c) => (
                  <div key={c.id} className="flex items-center gap-3 glass-card p-3.5">
                    <Award size={18} className={c.id === 'professional' ? 'text-brand-secondary' : 'text-[#ca8a04]'} />
                    <span className="text-sm font-semibold flex-1">{c.name}</span>
                    <span className="text-xs font-mono text-text-muted">{c.code}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6">
                <Button to="/training/certification" variant="secondary" arrow>
                  Certification requirements
                </Button>
              </div>
            </div>
            <ApplicationForm />
          </div>
        </Container>
      </section>
    </>
  );
}
